import React, { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, AlertTriangle, ChevronUp, ChevronDown, Users, Ship } from "lucide-react";
import { api } from "../lib/api";
import useEscClose from "../hooks/useEscClose";
import FormError from "./ui/FormError";

/**
 * MarkerOutOfRangeModal — drilldown table opened by double-clicking a
 * marker in the Reference-Ranges admin modal. Shows every athlete whose
 * latest lab flagged this marker outside the reference range.
 *
 * Columns: Athlete · Squad · Class · Value (with low/high status pill) ·
 * Test date. Column headers are click-to-sort. A top-of-modal toggle
 * lets the coach switch between "latest lab only" and "any historical
 * lab" — same endpoint, different `scope`.
 *
 * Intentionally lightweight: read-only. Editing a marker's value still
 * happens in the Data Editor / Lab Scan wizard — this modal is for
 * quickly triaging "who's low on B12" without exiting the ranges
 * screen.
 */
export default function MarkerOutOfRangeModal({ open, markerKey, markerLabel, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [scope, setScope] = useState("latest");
  const [sortKey, setSortKey] = useState("value");
  const [sortDir, setSortDir] = useState("desc");

  useEscClose(open, onClose);

  useEffect(() => {
    if (!open || !markerKey) return;
    setError("");
    setLoading(true);
    api
      .get(`/param/${encodeURIComponent(markerKey)}/out-of-range`, { params: { scope } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || e.message || "Could not load"))
      .finally(() => setLoading(false));
  }, [open, markerKey, scope]);

  const rows = useMemo(() => {
    if (!data?.athletes) return [];
    const a = [...data.athletes];
    const cmp = (x, y) => {
      const av = x[sortKey];
      const bv = y[sortKey];
      if (av === bv) return 0;
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      const na = typeof av === "number" ? av : String(av).toLowerCase();
      const nb = typeof bv === "number" ? bv : String(bv).toLowerCase();
      return na < nb ? -1 : 1;
    };
    a.sort(cmp);
    if (sortDir === "desc") a.reverse();
    return a;
  }, [data, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("desc"); }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[210] bg-[#040D1A]/90 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-6 px-3"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="marker-oor-modal"
    >
      <div className="card w-full max-w-4xl p-5 md:p-6">
        <div className="flex items-start justify-between mb-3 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
              <AlertTriangle size={11} /> Out of range · drill-down
            </div>
            <div className="font-display text-lg text-slate-100 mt-0.5">
              {markerLabel || markerKey}{" "}
              <span className="text-slate-500 text-[11px] font-mono-data">
                · {markerKey}
              </span>
            </div>
            {data && (
              <div className="text-[11px] text-slate-400 mt-1 font-mono-data">
                Reference {data.range_low ?? "—"} – {data.range_high ?? "—"} {data.unit}
                <span className="text-slate-500 ml-2">
                  · {data.count} athlete{data.count === 1 ? "" : "s"} flagged
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="flex items-center rounded-lg border border-[#1E304B] bg-[#0B1627] p-0.5 text-[10px]">
              <button
                onClick={() => setScope("latest")}
                className={`px-2 py-1 rounded ${scope === "latest" ? "bg-cyan-500/20 text-cyan-200" : "text-slate-400"}`}
                data-testid="marker-oor-scope-latest"
              >
                Latest lab
              </button>
              <button
                onClick={() => setScope("all")}
                className={`px-2 py-1 rounded ${scope === "all" ? "bg-cyan-500/20 text-cyan-200" : "text-slate-400"}`}
                data-testid="marker-oor-scope-all"
              >
                All history
              </button>
            </div>
            <button onClick={onClose} className="pill !p-1.5" data-testid="marker-oor-close">
              <X size={14} />
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-3">
            <FormError message={error} onDismiss={() => setError("")} testId="marker-oor-error" />
          </div>
        )}

        {loading ? (
          <div className="py-10 text-center text-slate-400">
            <Loader2 className="animate-spin inline mr-2" size={14} /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-emerald-300 text-[13px]">
            No athletes flagged{" "}
            <span className="text-slate-500">
              ({scope === "latest" ? "based on latest lab" : "across all history"})
            </span>
            .
          </div>
        ) : (
          <div className="rounded-lg border border-[#1E304B] max-h-[60vh] overflow-y-auto" data-testid="marker-oor-table-wrapper">
            <table className="w-full text-[12px]">
              <thead className="sticky top-0 bg-[#0B1627] z-10">
                <tr className="text-[10px] uppercase tracking-widest text-slate-500">
                  <SortableTh label="Athlete" k="name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortableTh label={<span className="flex items-center gap-1"><Users size={9} /> Squad</span>} k="group" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortableTh label={<span className="flex items-center gap-1"><Ship size={9} /> Class</span>} k="subgroup" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortableTh label="Value" k="value" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="right" />
                  <SortableTh label="Status" k="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortableTh label="Test date" k="date" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={`${r.sailor_id}-${r.record_id}-${i}`}
                    className="border-b border-[#1E304B] last:border-b-0 hover:bg-[#0F1E33]/50"
                    data-testid={`marker-oor-row-${r.sailor_id}`}
                  >
                    <td className="px-3 py-1.5 text-slate-100 font-mono-data whitespace-nowrap">
                      {r.name}
                    </td>
                    <td className="px-3 py-1.5 text-[11px] text-slate-400">{r.group || "—"}</td>
                    <td className="px-3 py-1.5 text-[11px] text-cyan-200/80 font-mono-data">
                      {r.subgroup || "—"}
                    </td>
                    <td className="px-3 py-1.5 text-right text-slate-100 font-mono-data">
                      {r.value}
                      {r.unit && <span className="text-slate-500 text-[10px] ml-1">{r.unit}</span>}
                    </td>
                    <td className="px-3 py-1.5">
                      <StatusPill status={r.status} />
                    </td>
                    <td className="px-3 py-1.5 text-[11px] text-slate-400 font-mono-data">
                      {r.date || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

function SortableTh({ label, k, sortKey, sortDir, onSort, align = "left" }) {
  const active = sortKey === k;
  return (
    <th
      onClick={() => onSort(k)}
      className={`px-3 py-2 border-b border-[#1E304B] cursor-pointer select-none ${align === "right" ? "text-right" : "text-left"} ${active ? "text-cyan-300" : "text-slate-500"} hover:text-slate-200`}
      data-testid={`marker-oor-sort-${k}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active && (sortDir === "asc" ? <ChevronUp size={9} /> : <ChevronDown size={9} />)}
      </span>
    </th>
  );
}

function StatusPill({ status }) {
  const cfg =
    status === "low"
      ? { bg: "rgba(250,204,21,0.15)", br: "rgba(250,204,21,0.5)", fg: "#FDE68A", label: "LOW" }
      : status === "high"
        ? { bg: "rgba(239,68,68,0.15)", br: "rgba(239,68,68,0.5)", fg: "#FCA5A5", label: "HIGH" }
        : { bg: "rgba(148,163,184,0.12)", br: "#1E304B", fg: "#CBD5E1", label: status?.toUpperCase() || "—" };
  return (
    <span
      className="inline-block px-2 py-0.5 rounded-full text-[10px] font-mono-data"
      style={{ background: cfg.bg, border: `1px solid ${cfg.br}`, color: cfg.fg }}
    >
      {cfg.label}
    </span>
  );
}
